import { useCallback } from 'react';
import { useAppContext } from '../contexts/AppContext';

class UndoableAction {
  constructor(description, undoFn, redoFn, affectedIndices) {
    this.description = description;
    this.timestamp = Date.now();
    this.undoFn = undoFn;
    this.redoFn = redoFn;
    this.affectedIndices = affectedIndices;
  }
}

export function useUndoRedo() {
  const { undoStack, setUndoStack, redoStack, setRedoStack } = useAppContext();

  const recordAction = useCallback((description, undoFn, redoFn, affectedIndices) => {
    const action = new UndoableAction(description, undoFn, redoFn, affectedIndices);
    setUndoStack(prev => [...prev.slice(-49), action]); // Keep max 50
    setRedoStack([]); // Clear redo on new action
  }, [setUndoStack, setRedoStack]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return null;

    const action = undoStack[undoStack.length - 1];
    action.undoFn();

    setUndoStack(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, action]);

    return action.description;
  }, [undoStack, setUndoStack, setRedoStack]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return null;

    const action = redoStack[redoStack.length - 1];
    action.redoFn();

    setRedoStack(prev => prev.slice(0, -1));
    setUndoStack(prev => [...prev, action]);

    return action.description;
  }, [redoStack, setUndoStack, setRedoStack]);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  return { recordAction, undo, redo, canUndo, canRedo, undoStack, redoStack };
}
